import { RefObject, useLayoutEffect, useState } from "react";

// Height reserved below the paper for the pager strip, so the scaled page plus
// its pager both fit the viewport without a scroll of their own.
export const INVOICE_PREVIEW_PAGER_RESERVE_PX = 56;

interface Args {
    viewportRef: RefObject<HTMLDivElement | null>;
    // Unscaled paper dimensions for the current page size.
    logicalPaper: { width: number; height: number };
    showPager: boolean;
    // Also scale the paper down to the viewport's height so the full page is
    // visible without scrolling. Disable where the viewport has no definite
    // height (its height follows the paper's), which would feed the scale back
    // into itself.
    fitToViewportHeight?: boolean;
}

// Scales the invoice preview paper to fit its viewport, tracking viewport size
// changes via a ResizeObserver. Shared by the edit-driven preview and the
// standalone read-only preview so the two viewers can't drift.
export function useInvoicePreviewPaperScale({
    viewportRef,
    logicalPaper,
    showPager,
    fitToViewportHeight = true,
}: Args): number {
    const [paperScale, setPaperScale] = useState(1);

    useLayoutEffect(
        function syncPaperScaleToViewport() {
            const viewport = viewportRef.current;
            if (!viewport) {
                return undefined;
            }

            const syncPaperScale = (
                viewportWidth: number,
                viewportHeight: number,
            ) => {
                if (viewportWidth <= 0) {
                    return;
                }
                const pagerReservePx = showPager
                    ? INVOICE_PREVIEW_PAGER_RESERVE_PX
                    : 0;
                const heightScale =
                    fitToViewportHeight && viewportHeight > 0
                        ? viewportHeight /
                          (logicalPaper.height + pagerReservePx)
                        : 1;
                setPaperScale(
                    Math.min(
                        1,
                        viewportWidth / logicalPaper.width,
                        heightScale,
                    ),
                );
            };

            const rect = viewport.getBoundingClientRect();
            syncPaperScale(rect.width, rect.height);
            const observer = new ResizeObserver((entries) => {
                const entry = entries[0];
                if (entry === undefined) {
                    return;
                }
                syncPaperScale(
                    entry.contentRect.width,
                    entry.contentRect.height,
                );
            });
            observer.observe(viewport);
            return () => observer.disconnect();
        },
        [
            viewportRef,
            logicalPaper.width,
            logicalPaper.height,
            showPager,
            fitToViewportHeight,
        ],
    );

    return paperScale;
}
